import uuid from 'utils/uuid';
import {
  getAssetUrl,
  getInitialPlaceholderData,
  getSampleBookPage,
  getTemplatePage,
  isPhotoPlaceholder,
  isTextPlaceholder,
} from 'editor-utils/template';

/**
 * Merge document-level page defaults (from additionalInfo.pageDefaults) into a
 * freshly created page's `override`. Used so that the "Apply to all pages"
 * background choice also carries over to pages added later. Existing per-page
 * override values win over the defaults.
 * @param {object} page
 * @param {object} [pageDefaults]
 * @returns {object}
 */
export const applyPageDefaults = (page, pageDefaults) => {
  if (!pageDefaults || Object.keys(pageDefaults).length === 0) {
    return page;
  }
  return {
    ...page,
    override: {
      ...pageDefaults,
      ...page.override,
    },
  };
};

/**
 * Split a draft's pages into the content pages a generator works over and the
 * generator's own pages (TOC / index), pulled out by template page id.
 * Centralizes the cover-strip + first-match `startIndex` both the TOC and index
 * generators otherwise repeat verbatim.
 * @param {object} template
 * @param {object} item
 * @param {Array<string>} pageIds  template page ids to pull out (e.g. tocPageIds)
 * @returns {{ contentPages: Array, removedPages: Array, startIndex: number }}
 */
export const splitGeneratorPages = (template, item, pageIds = []) => {
  const pages = [...(item.pages ?? [])];
  if (template.cover) {
    pages.shift();
  }

  let startIndex;
  const removedPages = [];
  const contentPages = pages.filter((page, index) => {
    if (pageIds.indexOf(page.id) >= 0) {
      if (startIndex == null) {
        startIndex = index;
      }
      removedPages.push(page);
      return false;
    }
    return true;
  });

  return { contentPages, removedPages, startIndex: startIndex ?? 0 };
};

const RUNNING_FOOT_ID = 'pedigree-running-foot';

/**
 * Running foot of the redesigned books ("THE … FAMILY") for the ToC request.
 * The server cannot read it from the request pages (generated placeholders
 * are not sent), so it is sent on its own: the one of the ToC (the user may
 * have edited it), else the one of any other page, for a ToC page the
 * regeneration adds.
 * @param {Array<object>} pages ToC pages first.
 * @returns {string|undefined}
 */
export const runningFootOf = (pages) =>
  pages
    .flatMap(page => page.placeholders ?? [])
    .find(placeholder => placeholder.id === RUNNING_FOOT_ID && placeholder.data?.value)
    ?.data.value;

// Letter spacing and line height of the shared page header and footer of the redesigned
// books, as the generator writes them (parabo-serverless src/lib/book/heading.js). Neither
// template styles nor the template schema can carry them, so the editor stores them on the
// draft placeholders of the pages it creates (the blank page).
const TEXT_EFFECTS = {
  'classic.pedigree-eyebrow': { letterSpacing: 0.21 },
  'classic.pedigree-title': { lineHeight: 1.05 },
  'classic.pedigree-running-foot': { letterSpacing: 0.25 },
};

/**
 * Generate common Page object
 * @param {object} page
 * @param {object} opts
 * @param {number} opts.pageNumber
 * @param {string} [opts.runningFoot] "THE … FAMILY" for a `pedigree-running-foot` placeholder
 * @returns {object}
 */
export const generatePage = (page, { pageNumber, runningFoot } = {}) => {
  const placeholderData = placeholder => {
    if (isTextPlaceholder(placeholder)) {
      if (placeholder.id === 'page-num' && pageNumber != null) {
        if (Number.isInteger(pageNumber) && pageNumber > 0) {
          return { value: `${pageNumber}` };
        }
      }
      if (placeholder.id === RUNNING_FOOT_ID && runningFoot) {
        return { value: runningFoot };
      }
    }

    return getInitialPlaceholderData(placeholder);
  };

  return {
    key: uuid(),
    id: page.id,
    tags: page.tags ?? [],
    placeholders: (page.placeholders ?? []).map(placeholder => ({
      id: placeholder.id,
      type: placeholder.type,
      data: placeholderData(placeholder),
      ...(TEXT_EFFECTS[placeholder.styleName] ? { polotno: TEXT_EFFECTS[placeholder.styleName] } : {}),
    })),
  };
};

/**
 * Transform section (pages or cover) from server to client.
 * @param {object} template
 * @param {string} type
 * @param {Array} originalPhotos
 * @param {Array} pages
 * @returns {Array}
 */
export const transformPagesFromServer = (template, type = 'pages', originalPhotos, pages = []) => {
  const templatePagesIds = template[type].map(page => page.id);

  return pages.map(({ placeholders: generatedPlaceholders, specs, ...page }) => {
    let pageId;
    // page.id should be Array of supported pageIds or String pageId
    if (Array.isArray(page.id)) {
      // when page.id is array, find first supported page from template
      pageId = page.id.find(id => templatePagesIds.indexOf(id) >= 0);
    } else {
      pageId = page.id;
    }

    if (!pageId) {
      // TODO Unknown page
      console.log('Unknown page for this SKU', page.id);
      pageId = templatePagesIds[0];
    }

    const placeholderData = (item = {}) => {
      if (isPhotoPlaceholder(item)) {
        let photoKey = item.photo_key;
        if (!photoKey && item.defaultValue) {
          photoKey = item.defaultValue;
        }

        return {
          photo_key: photoKey,
          x: item.x ?? 0.5,
          y: item.y ?? 0.5,
          scale: item.scale ?? 1,
          orientation: item.orientation ?? 0,
        };
      }

      if (isTextPlaceholder(item)) {
        return {
          value: item.value,
          value_enhanced: item.value,
          value_original: item.value_original || item.value,
        };
      }

      return {};
    };

    const templatePage = getTemplatePage(template, pageId, type);

    const placeholders = templatePage.placeholders.map((placeholder, sortIndex) => ({
      id: placeholder.id,
      type: placeholder.type,
      data: getInitialPlaceholderData(placeholder),
      sortIndex: sortIndex + 0.5,
    }));

    (generatedPlaceholders ?? []).forEach((item, sortIndex) => {
      let placeholderIndex = placeholders.findIndex(
        placeholder => placeholder.id === item.placeholder_id,
      );
      if (placeholderIndex < 0) {
        placeholderIndex =
          placeholders.push({
            id: item.placeholder_id,
            type: item.type,
            sortIndex,
          }) - 1;
      }
      placeholders[placeholderIndex].data = placeholderData(item);

      const placeholderId = placeholders[placeholderIndex].id;

      // TODO spec should be a part of generatedPlaceholder
      const spec = (specs ?? []).find(s => s.id === placeholderId);
      if (spec) {
        const { polotno, ...override } = spec;
        placeholders[placeholderIndex].override = override;
        placeholders[placeholderIndex].polotno = polotno ?? undefined;
      }
    });

    for (let index = placeholders.length - 1; index >= 0; index -= 1) {
      if (!('sortIndex' in placeholders[index])) {
        const nextPlaceholder = placeholders[index + 1] ?? {};
        if ('sortIndex' in nextPlaceholder) {
          placeholders[index].sortIndex = nextPlaceholder.sortIndex - 0.1;
        }
      }
    }

    // sort placeholders, respect generator order, then rest of placeholders.
    placeholders.sort((a, b) => {
      const sortIndexA = a.sortIndex ?? placeholders.length;
      const sortIndexB = b.sortIndex ?? placeholders.length;

      return sortIndexA < sortIndexB ? -1 : 1;
    });

    return {
      ...page,
      id: pageId,
      type,
      placeholders: placeholders
        .map(({ sortIndex, ...placeholder }) => placeholder)
        .filter(p => p.id.indexOf('generator') !== 0),
      tags: page.tags ?? [],
    };
  });
};

/**
 * Transform server generated data to client data.
 * @param {object} template
 * @param {Array} photos
 * @param {object} data
 * @returns {Array}
 */
export const transformPages = (template, photos, data) => {
  if (template.cover && data.cover) {
    const pages = [
      ...transformPagesFromServer(template, 'cover', photos, [data.cover]),
      ...transformPagesFromServer(template, 'pages', photos, data.pages),
    ];

    // When number of [cover, ...pages] is even, add new page, this number has to be odd
    if (pages.length % 2 === 0) {
      pages.push(generatePage(getSampleBookPage(template)));
    }

    return pages;
  }

  return transformPagesFromServer(template, 'pages', photos, data.pages);
};

/**
 * Extract photos from server generated pages
 * @param {object} template
 * @param {Array} pages
 * @returns {Array}
 */
export const extractPhotosFromPages = (template, pages = []) => {
  const photos = [];

  pages.forEach(page => {
    (page.placeholders || []).forEach(placeholder => {
      if (isPhotoPlaceholder(placeholder)) {
        let defaultValue;

        let { photo_key: key, photo_url: url } = placeholder;
        if (!url && placeholder.defaultValue) {
          defaultValue = placeholder.defaultValue;
          key = defaultValue;
          url = getAssetUrl(template, `/assets_client/images/${defaultValue}`);
        }

        photos.push({
          key,
          url,
          width: placeholder.width || undefined,
          height: placeholder.height || undefined,
          alive: 1,
          datetime: 'N/A',
          tags: placeholder.tags || [],
          type: placeholder.photo_mime_type || undefined,
          defaultValue,
        });
      }
    });
  });

  return photos;
};

/**
 * Generate new page, based on previous one.
 * @param {object} template
 * @param {object} previousPage
 * @param {string} newPageId
 * @param {string} type
 * @returns {object}
 */
export function generateUpdatedPage(template, previousPage, newPageId, type = 'pages') {
  const templatePage = getTemplatePage(template, newPageId, type);

  const placeholders = templatePage.placeholders.map(placeholder => {
    // We need to use some data from previousPage
    let data = getInitialPlaceholderData(placeholder);

    const previousPlaceholder = previousPage.placeholders.find(p => p.id === placeholder.id);
    if (previousPlaceholder?.data) {
      // A stored value (even an empty one the user cleared) wins over the new layout's default text.
      data = {
        ...data,
        ...previousPlaceholder.data,
      };

      // Reset photo data when layout is changed
      if (isPhotoPlaceholder(placeholder)) {
        data = {
          ...data,
          x: 0.5,
          y: 0.5,
          scale: 1,
        };
      }
    }

    return {
      id: placeholder.id,
      type: placeholder.type,
      data,

      // TODO is usefull to keep override from previous placeholder?
      // override: placeholder.override
    };
  });

  // Hybrid carry-over (parabo-swift switchLayout semantics): photos whose
  // placeholder id has no counterpart in the new layout fill the remaining
  // empty photo slots positionally; photos that don't fit disappear from the
  // page (they stay in the item's photo list).
  const placedKeys = new Set(
    placeholders
      .filter(placeholder => isPhotoPlaceholder(placeholder) && placeholder.data.photo_key)
      .map(placeholder => placeholder.data.photo_key),
  );

  const unplaced = previousPage.placeholders.filter(
    placeholder =>
      isPhotoPlaceholder(placeholder) &&
      placeholder.data?.photo_key &&
      !placedKeys.has(placeholder.data.photo_key),
  );

  placeholders.forEach(placeholder => {
    if (!isPhotoPlaceholder(placeholder) || placeholder.data.photo_key) {
      return;
    }

    const source = unplaced.shift();
    if (!source) {
      return;
    }

    placeholder.data = {
      ...source.data,
      x: 0.5,
      y: 0.5,
      scale: 1,
    };
  });

  return {
    ...previousPage,
    id: newPageId,
    placeholders,
  };
}
